import {
  startTelemetryPipeline,
  telemetryIsEnabled,
  trackTelemetryError,
  trackTelemetryEvent,
} from '@/lib/observability/telemetry';

let initialized = false;

function installGlobalErrorCapture(): void {
  window.addEventListener('error', (event) => {
    const source = event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : 'unknown';

    trackTelemetryError(event.error || event.message, {
      source,
      type: 'window.error',
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    trackTelemetryError(event.reason, {
      type: 'window.unhandledrejection',
    });
  });
}

function installNavigationTimingCapture(): void {
  if (document.readyState === 'complete') {
    captureNavigationTiming();
    return;
  }

  window.addEventListener(
    'load',
    () => {
      captureNavigationTiming();
    },
    { once: true }
  );
}

function captureNavigationTiming(): void {
  const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
  if (!nav) return;

  const dnsLookup = Math.max(0, nav.domainLookupEnd - nav.domainLookupStart);
  const connect = Math.max(0, nav.connectEnd - nav.connectStart);
  const ttfb = Math.max(0, nav.responseStart - nav.requestStart);
  const download = Math.max(0, nav.responseEnd - nav.responseStart);
  const domInteractive = Math.max(0, nav.domInteractive - nav.startTime);
  const domComplete = Math.max(0, nav.domComplete - nav.startTime);

  trackTelemetryEvent({
    name: 'web.navigation.timing',
    level: 'info',
    metrics: {
      dnsLookup,
      connect,
      ttfb,
      download,
      domInteractive,
      domComplete,
    },
    context: {
      route: window.location.pathname,
      transferSize: Number.isFinite(nav.transferSize) ? nav.transferSize : 0,
      encodedBodySize: Number.isFinite(nav.encodedBodySize) ? nav.encodedBodySize : 0,
    },
  });
}

function installWebVitalsCapture(): void {
  if (typeof PerformanceObserver === 'undefined') return;

  captureLargestContentfulPaint();
  captureCumulativeLayoutShift();
  captureFirstInputDelay();
  captureLongTasks();
}

function captureLargestContentfulPaint(): void {
  let latestLcp = 0;

  try {
    const observer = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        latestLcp = entry.startTime;
      }
    });

    observer.observe({ type: 'largest-contentful-paint', buffered: true });

    const report = () => {
      if (latestLcp <= 0) return;
      trackTelemetryEvent({
        name: 'web.vital.lcp',
        level: 'info',
        durationMs: latestLcp,
        metrics: {
          lcp: latestLcp,
        },
        context: {
          route: window.location.pathname,
        },
      });
      observer.disconnect();
    };

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        report();
      }
    });

    window.addEventListener('pagehide', report, { once: true });
  } catch {
    // Browser does not support buffered LCP observations.
  }
}

function captureCumulativeLayoutShift(): void {
  let cls = 0;

  try {
    const observer = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries() as Array<PerformanceEntry & { value?: number; hadRecentInput?: boolean }>) {
        if (!entry.hadRecentInput && typeof entry.value === 'number') {
          cls += entry.value;
        }
      }
    });

    observer.observe({ type: 'layout-shift', buffered: true });

    const report = () => {
      if (cls <= 0) return;
      trackTelemetryEvent({
        name: 'web.vital.cls',
        level: 'info',
        metrics: {
          cls: Number(cls.toFixed(4)),
        },
        context: {
          route: window.location.pathname,
        },
      });
      observer.disconnect();
    };

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        report();
      }
    });

    window.addEventListener('pagehide', report, { once: true });
  } catch {
    // Browser does not support CLS observations.
  }
}

function captureFirstInputDelay(): void {
  try {
    const observer = new PerformanceObserver((entryList) => {
      const firstInput = entryList.getEntries()[0] as PerformanceEntry & {
        processingStart?: number;
        startTime: number;
      };
      if (!firstInput) return;

      const fid = Math.max(0, (firstInput.processingStart || firstInput.startTime) - firstInput.startTime);

      trackTelemetryEvent({
        name: 'web.vital.fid',
        level: 'info',
        durationMs: fid,
        metrics: {
          fid,
        },
        context: {
          route: window.location.pathname,
        },
      });

      observer.disconnect();
    });

    observer.observe({ type: 'first-input', buffered: true });
  } catch {
    // Browser does not support FID observations.
  }
}

function captureLongTasks(): void {
  try {
    const observer = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        if (entry.duration < 120) continue;
        trackTelemetryEvent({
          name: 'web.longtask',
          level: 'warn',
          durationMs: entry.duration,
          metrics: {
            durationMs: Math.round(entry.duration),
          },
          context: {
            route: window.location.pathname,
          },
        });
      }
    });

    observer.observe({ type: 'longtask', buffered: true });
  } catch {
    // Browser does not support Long Task API.
  }
}

export function initializeTelemetry(): void {
  if (initialized) return;
  initialized = true;

  if (!telemetryIsEnabled()) {
    return;
  }

  startTelemetryPipeline();
  installGlobalErrorCapture();
  installNavigationTimingCapture();
  installWebVitalsCapture();

  trackTelemetryEvent({
    name: 'telemetry.bootstrap.ready',
    level: 'info',
    context: {
      route: window.location.pathname,
      online: navigator.onLine,
      visibility: document.visibilityState,
    },
  });
}
